/**
 * The raise-query form: record summary, category chips, priority toggle, text field with the
 * world-space keyboard or dictation, submit. Opens next to the record it was raised from.
 */
import * as THREE from 'three';
import {
  QUERY_CATEGORIES,
  QUERY_PRIORITIES,
  CreateQueryInputSchema,
  type CreateQueryInput,
  type DashboardId,
  type Position,
  type QueryCategory,
  type QueryPriority,
  type RecordRef,
  type Role,
} from '@clinspace/shared';
import type { CanvasPanel, Region } from '../dashboards/CanvasPanel.ts';
import { PALETTE, chip, text, truncate, font, panelBackground } from '../dashboards/charts.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { Dictation, dictationAvailable } from './dictation.ts';
import { FloatingPanel, type FloatingPanelSounds } from './FloatingPanel.ts';
import { drawTextField } from './textField.ts';

export interface QueryDraft {
  ref: RecordRef;
  title: string;
  currentValue: string | undefined;
  dashboard: DashboardId;
  category: QueryCategory;
  priority: QueryPriority;
  text: string;
}

export interface QueryFormHandlers {
  submit(input: CreateQueryInput): Promise<void>;
  cancel(): void;
}

type FormRegion =
  | { kind: 'category'; value: QueryCategory }
  | { kind: 'priority'; value: QueryPriority }
  | { kind: 'action'; action: 'submit' | 'cancel' | 'dictate' | 'field' | 'suggest' };

/** A sensible default category from where the query is raised. */
export function defaultCategory(dashboard: DashboardId, field?: string): QueryCategory {
  if (field === 'Missed visit reason' || field?.startsWith('Missing')) return 'Missing data';
  switch (dashboard) {
    case 'MissingData':
    case 'DataCleaning':
      return 'Missing data';
    case 'LabAbnormality':
      return 'Out of range';
    case 'SAEReview':
      return 'Safety follow-up';
    case 'ProtocolDeviation':
    case 'VisitCompliance':
      return 'Protocol question';
    default:
      return 'Data discrepancy';
  }
}

export function validateDraft(draft: QueryDraft): string | undefined {
  const t = draft.text.trim();
  if (t.length < 3) return 'Describe the query in at least three characters.';
  if (t.length > 4000) return 'The query text is too long.';
  return undefined;
}

export function draftToInput(
  draft: QueryDraft,
  raisedBy: { name: string; role: Role; sessionId: string },
  siteId: string,
  position: Position,
): CreateQueryInput {
  const input: CreateQueryInput = {
    study: draft.ref.study,
    subjectId: draft.ref.subjectId,
    siteId,
    form: draft.ref.form,
    recordId: draft.ref.recordId,
    dashboard: draft.dashboard,
    category: draft.category,
    text: draft.text.trim(),
    priority: draft.priority,
    raisedBy,
    position,
    source: 'vr',
  };
  if (draft.ref.visit) input.visit = draft.ref.visit;
  if (draft.ref.field) input.field = draft.ref.field;
  if (draft.currentValue !== undefined) input.currentValue = draft.currentValue.slice(0, 1000);
  return CreateQueryInputSchema.parse(input);
}

export class QueryForm extends FloatingPanel {
  draft: QueryDraft | undefined;
  error: string | undefined;
  submitting = false;
  private readonly dictation: Dictation;
  readonly canDictate = dictationAvailable();
  /** Set by the host when the assistant is on: returns suggested text for the open draft. */
  suggest: ((draft: QueryDraft) => Promise<string | undefined>) | undefined;
  suggesting = false;

  constructor(
    interactive: InteractiveSystem,
    private readonly handlers: QueryFormHandlers,
    sounds?: FloatingPanelSounds,
  ) {
    super(
      interactive,
      { width: 1.0, height: 0.72, pixelWidth: 700, pixelHeight: 504, name: 'query-form' },
      sounds,
    );
    this.dictation = new Dictation({
      onInterim: (t) => {
        this.field.interim = t;
        this.panel.invalidate();
      },
      onFinal: (t) => {
        this.field.interim = '';
        if (t)
          this.field.input((this.field.value && !this.field.value.endsWith(' ') ? ' ' : '') + t);
        this.panel.invalidate();
      },
      onEnd: (error) => {
        this.field.interim = '';
        if (error) this.error = `Dictation stopped: ${error}`;
        this.panel.invalidate();
      },
    });
  }

  open(draft: QueryDraft, origin: THREE.Vector3, viewer: THREE.Vector3): void {
    this.draft = { ...draft };
    this.field.clear();
    this.field.value = draft.text;
    this.error = undefined;
    this.submitting = false;
    this.place(origin, viewer);
    this.show();
    this.setTyping(true);
  }

  override hide(): void {
    this.dictation.stop();
    super.hide();
  }

  /** The world position recorded with the query (where the form floats). */
  worldPosition(): Position {
    const p = this.group.getWorldPosition(new THREE.Vector3());
    return { x: round(p.x), y: round(p.y), z: round(p.z) };
  }

  setText(value: string): void {
    this.field.value = value;
    this.panel.invalidate();
  }

  setCategory(value: QueryCategory): void {
    if (this.draft) this.draft.category = value;
    this.panel.invalidate();
  }

  setPriority(value: QueryPriority): void {
    if (this.draft) this.draft.priority = value;
    this.panel.invalidate();
  }

  async submit(): Promise<boolean> {
    if (!this.draft || this.submitting) return false;
    this.draft.text = this.field.trimmed;
    this.error = validateDraft(this.draft);
    this.panel.invalidate();
    if (this.error) return false;
    this.submitting = true;
    this.dictation.stop();
    try {
      // The host builds the full input (identity, site, position) and posts it.
      await this.handlers.submit(this.buildInput());
      this.hide();
      return true;
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
      this.submitting = false;
      this.panel.invalidate();
      return false;
    }
  }

  /** Set by the host before use: resolves identity, site and position. */
  buildInput: () => CreateQueryInput = () => {
    throw new Error('QueryForm.buildInput not configured');
  };

  cancel(): void {
    this.hide();
    this.handlers.cancel();
  }

  /** Asks the assistant for query text and puts it in the field (the user still sends it). */
  async requestSuggestion(): Promise<string | undefined> {
    if (!this.suggest || !this.draft || this.suggesting) return undefined;
    this.suggesting = true;
    this.error = undefined;
    this.panel.invalidate();
    try {
      const text = await this.suggest(this.draft);
      if (text) this.setText(text);
      else this.error = 'The assistant had no suggestion for this record.';
      return text;
    } catch (err) {
      this.error = `Suggestion failed: ${(err as Error).message}`;
      return undefined;
    } finally {
      this.suggesting = false;
      this.panel.invalidate();
    }
  }

  toggleDictation(): void {
    if (this.dictation.active) this.dictation.stop();
    else if (!this.dictation.start()) this.error = 'Dictation is not available in this browser.';
    this.panel.invalidate();
  }

  state(): {
    open: boolean;
    text: string;
    category?: QueryCategory;
    priority?: QueryPriority;
    error?: string;
    dictating: boolean;
  } {
    return {
      open: this.isOpen,
      text: this.field.value,
      category: this.draft?.category,
      priority: this.draft?.priority,
      error: this.error,
      dictating: this.dictation.active,
    };
  }

  protected onEnter(): void {
    void this.submit();
  }

  protected onEscape(): void {
    this.cancel();
  }

  protected draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    const d = this.draft;
    panelBackground(ctx, W, H, 16);
    ctx.strokeStyle = PALETTE.tealDim;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(1.5, 1.5, W - 3, H - 3, 16);
    ctx.stroke();
    text(ctx, 'Raise a query', 20, 32, { size: 17, weight: 700 });
    if (!d) return;
    const ref = d.ref;
    ctx.font = font(12);
    text(ctx, truncate(ctx, d.title, W - 40), 20, 56, {
      size: 12,
      colour: PALETTE.text,
      weight: 600,
    });
    const where = [ref.study, ref.subjectId, ref.visit, ref.form, ref.field]
      .filter(Boolean)
      .join(' · ');
    text(ctx, truncate(ctx, where, W - 40), 20, 76, { size: 11, colour: PALETTE.muted });
    if (d.currentValue !== undefined)
      text(ctx, truncate(ctx, `Current value: ${d.currentValue}`, W - 40), 20, 94, {
        size: 11,
        colour: PALETTE.muted,
      });

    // Category chips (two rows of three).
    text(ctx, 'CATEGORY', 20, 120, { size: 10, colour: PALETTE.muted, weight: 600 });
    let x = 20;
    let y = 128;
    QUERY_CATEGORIES.forEach((c, i) => {
      if (i === 3) {
        x = 20;
        y += 30;
      }
      const id = `cat:${c}`;
      const r = chip(ctx, c, x, y, {
        active: d.category === c,
        hovered: panel.isHovered(id),
        h: 24,
        size: 11,
        minW: 120,
      });
      panel.region<FormRegion>({ id, ...r, data: { kind: 'category', value: c } });
      x += r.w + 8;
    });
    // Priority.
    text(ctx, 'PRIORITY', 440, 120, { size: 10, colour: PALETTE.muted, weight: 600 });
    x = 440;
    QUERY_PRIORITIES.forEach((p) => {
      const id = `pri:${p}`;
      const colour = p === 'High' ? PALETTE.crimson : p === 'Low' ? PALETTE.grey : PALETTE.teal;
      const r = chip(ctx, p, x, 128, {
        active: d.priority === p,
        hovered: panel.isHovered(id),
        h: 24,
        size: 11,
        colour,
      });
      panel.region<FormRegion>({ id, ...r, data: { kind: 'priority', value: p } });
      x += r.w + 8;
    });

    // Text field.
    const fieldRect = { x: 20, y: 196, w: W - 40, h: 220 };
    drawTextField(ctx, fieldRect, this.field, {
      placeholder: this.typing ? 'Type with the keyboard below, or dictate.' : 'Select to type.',
      active: this.typing,
      size: 13,
    });
    panel.region<FormRegion>({
      id: 'field',
      ...fieldRect,
      data: { kind: 'action', action: 'field' },
      selectable: false,
    });

    // Actions.
    const submit = chip(ctx, this.submitting ? 'Sending…' : 'Raise query', W - 150, H - 48, {
      active: true,
      hovered: panel.isHovered('submit'),
      h: 30,
      size: 13,
      colour: PALETTE.teal,
    });
    panel.region<FormRegion>({
      id: 'submit',
      ...submit,
      data: { kind: 'action', action: 'submit' },
    });
    const cancel = chip(ctx, 'Cancel', W - 150 - 90, H - 48, {
      hovered: panel.isHovered('cancel'),
      h: 30,
      size: 13,
    });
    panel.region<FormRegion>({
      id: 'cancel',
      ...cancel,
      data: { kind: 'action', action: 'cancel' },
    });
    if (this.suggest) {
      const r = chip(
        ctx,
        this.suggesting ? 'Suggesting…' : 'Suggest text',
        W - 150 - 90 - 118,
        H - 48,
        {
          hovered: panel.isHovered('suggest'),
          h: 30,
          size: 13,
          colour: PALETTE.navy,
          active: this.suggesting,
        },
      );
      panel.region<FormRegion>({
        id: 'suggest',
        ...r,
        data: { kind: 'action', action: 'suggest' },
        tooltip: ['The assistant drafts query text for this record; edit or ignore it'],
      });
    }
    if (this.canDictate) {
      const dictating = this.dictation.active;
      const r = chip(ctx, dictating ? 'Stop dictation' : 'Dictate', 20, H - 48, {
        active: dictating,
        hovered: panel.isHovered('dictate'),
        h: 30,
        size: 13,
        colour: PALETTE.amber,
      });
      panel.region<FormRegion>({
        id: 'dictate',
        ...r,
        data: { kind: 'action', action: 'dictate' },
        tooltip: ['Push to talk: the transcript appears in the field for correction'],
      });
    } else {
      text(ctx, 'Dictation is not available here; use the keyboard.', 20, H - 28, {
        size: 10,
        colour: PALETTE.muted,
      });
    }
    if (this.error)
      text(ctx, this.error, 20, 436, { size: 11, colour: PALETTE.crimson, maxWidth: W - 40 });
    else
      text(ctx, `${this.field.value.trim().length} characters · Enter sends`, 20, 436, {
        size: 10,
        colour: PALETTE.muted,
      });
  }

  protected select(region: Region): void {
    const data = region.data as FormRegion | undefined;
    if (!data) return;
    this.sounds?.click();
    switch (data.kind) {
      case 'category':
        this.setCategory(data.value);
        break;
      case 'priority':
        this.setPriority(data.value);
        break;
      case 'action':
        if (data.action === 'submit') void this.submit();
        else if (data.action === 'cancel') this.cancel();
        else if (data.action === 'dictate') this.toggleDictation();
        else if (data.action === 'suggest') void this.requestSuggestion();
        else if (data.action === 'field') this.setTyping(true);
        break;
    }
  }
}

function round(v: number): number {
  return Math.round(v * 100) / 100;
}
